import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import {
  fetchTherapyOccurrenceRequests,
  markTherapyOccurrenceRequestsSeen,
} from '../api/therapyOccurrenceRequestsApi';
import { useRequestNotifications } from '../context/useRequestNotifications';
import { userPickupMethodOptions } from '../../users/constants/userConstants';
import { getTherapyOccurrenceRequestStatusLabel } from '../utils/therapyOccurrenceRequestPresentation';
import UpdateTherapyOccurrenceRequestStatusModal from '../components/modals/UpdateTherapyOccurrenceRequestStatusModal';
import useResize from '../../../shared/hooks/useResize';
import { useAuth } from '../../../context/useAuth';
import { PAGE_SIZE } from '../../../config';
import PostalFormModal from '../../postalForm/components/PostalFormModal';
import usePostalForm from '../../postalForm/hooks/usePostalForm';
import { formatNameLastFirst } from '../../../shared/utils/formatName';
import StatusFlag from '../../../components/elements/StatusFlag';
import TotalCountField from '../../../components/elements/TotalCountField';
import {
  getRequestStatusTone,
  STATUS_TONES,
} from '../../../shared/utils/statusTones';
import { formatUsername, isStaff } from '../../../shared/utils/roles';

const getOccurrenceStatusLabel = (status) => {
  const map = {
    available: 'Dostupna',
    unavailable: 'Nedostupna',
    taken: 'Preuzeta',
    saved: 'Sačuvana',
    reserved: 'Rezervisana',
    missed: 'Propuštena',
    unassigned: 'Nedodeljeno',
  };

  return map[status] || status || '-';
};

export default function TherapyOccurrenceRequestsPage() {
  const width = useResize();
  const { user } = useAuth();
  const postalForm = usePostalForm();
  const { unseenCount, setUnseenCount } = useRequestNotifications();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [requests, setRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState({ status: null, pickupMethod: null });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const latestRequestsRequestRef = useRef(0);
  // Zahtevi koji su bili novi kada su prikazani u ovoj poseti stranice. Bek ih
  // odmah oznaci kao vidjene, a oznaka "Novo" ostaje do napustanja stranice.
  const [freshRequestIds, setFreshRequestIds] = useState(() => new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadRequests = useCallback(async (page = currentPage) => {
    const requestId = latestRequestsRequestRef.current + 1;
    latestRequestsRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchTherapyOccurrenceRequests({
      page,
      limit: PAGE_SIZE,
      search: debouncedSearch || null,
      status: filter.status || null,
      pickupMethod: filter.pickupMethod || null,
    });

    if (requestId !== latestRequestsRequestRef.current) {
      return;
    }

    setListLoading(false);

    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;
      setRequests(data.requests || []);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
      setTotalResults(data?.pagination?.totalCount ?? null);
    } else if (message) {
      setMessage(message);
    }
  }, [currentPage, debouncedSearch, filter.pickupMethod, filter.status]);

  useEffect(() => {
    loadRequests(currentPage);
  }, [loadRequests]);

  // Prikazani novi zahtevi su sada vidjeni; bek vraca novi broj za badge.
  useEffect(() => {
    if (!isStaff(user)) return;
    const unseenIds = requests
      .filter((request) => request?.isSeen === false && request?._id)
      .map((request) => request._id);
    if (!unseenIds.length) return;

    setFreshRequestIds((prev) => {
      const next = new Set(prev);
      unseenIds.forEach((id) => next.add(id));
      return next;
    });
    markTherapyOccurrenceRequestsSeen(unseenIds).then(({ ok, data }) => {
      if (ok && data) setUnseenCount(data.count);
    });
  }, [requests, setUnseenCount, user]);

  // Stigao je novi zahtev dok je admin na stranici: osvezi listu (time se novi
  // zahtev odmah prikaze i oznaci kao vidjen). Smanjenje broja ne trazi reload.
  const previousUnseenCountRef = useRef(unseenCount);
  useEffect(() => {
    const previous = previousUnseenCountRef.current;
    previousUnseenCountRef.current = unseenCount;
    if (unseenCount > previous) {
      loadRequests();
    }
  }, [loadRequests, unseenCount]);

  const handleFilterChange = (newFilter) => {
    setCurrentPage(1);
    setFilter((prev) => ({ ...prev, ...newFilter }));
  };

  const requestFilterSections = [
    {
      title: '',
      type: 'status',
      buttonLabel: 'Status zahteva',
      items: [
        { key: 'pending', label: 'Na cekanju' },
        { key: 'sent', label: 'Poslato' },
        { key: 'cancelled', label: 'Otkazano' },
      ],
    },
  ];

  const pickupMethodFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'pickupMethod',
        buttonLabel: 'Nacin preuzimanja',
        multiSelect: true,
        popupClassName: 'pickup-method-filter-dropdown',
        items: userPickupMethodOptions.map((option) => ({
          key: option.value,
          label: option.label,
        })),
      },
    ],
    []
  );

  const columns = useMemo(() => {
    const baseColumns = [
      // {
      //   key: 'therapyName',
      //   label: 'Terapija',
      //   render: (val) => val || '-',
      // },
      {
        key: 'userName',
        label: 'Pacijent',
        // Prikaz "Prezime Ime" (bek šalje `userName` kao "Ime Prezime").
        render: (val, row) => {
          const name = formatNameLastFirst(row, '') || row?.userEmail || '-';
          if (!freshRequestIds.has(row?._id)) return name;
          return (
            <span
              style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}
            >
              {name}
              <StatusFlag tone={STATUS_TONES.positive}>Novo</StatusFlag>
            </span>
          );
        },
      },
      {
        key: 'therapyMedications',
        label: 'Lek',
        render: (val) =>
          Array.isArray(val) && val.length ? val.join(', ') : '-',
      },
      // {
      //   key: 'userName',
      //   label: 'Korisnik',
      //   render: (val, row) => val || row?.userEmail || '-',
      // },
      {
        key: 'userAddress',
        label: 'Adresa',
        render: (val) => val || '-',
      },
      {
        key: 'pickupMethod',
        label: 'Nacin preuzimanja',
        render: (val) => val || '-',
      },
      {
        key: 'status',
        label: 'Status zahteva',
        render: (val) => (
          <StatusFlag tone={getRequestStatusTone(val)}>
            {getTherapyOccurrenceRequestStatusLabel(val)}
          </StatusFlag>
        ),
      },
      {
        key: 'requestedDeliveryDate',
        label: 'Zeljeni datum dostave',
        render: (val) => (val ? dayjs(val).format('DD.MM.YYYY') : '-'),
      },
    ];

    if (width <= 768) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        key: 'note',
        label: 'Napomena apotekara',
        render: (val) => val || '-',
      },
      {
        // Ko je i kada prvi video zahtev na ovoj stranici.
        key: 'seenAt',
        label: 'Prvi video',
        render: (val, row) => {
          if (!val) return '-';
          const admin =
            row?.seenByName || formatUsername(row?.seenByUsername) || '-';
          return (
            <span>
              {admin}
              <br />
              {dayjs(val).format('DD.MM.YYYY HH:mm')}
            </span>
          );
        },
      },
    ];
  }, [freshRequestIds, width]);

  const handleRequestUpdated = async () => {
    await loadRequests();
  };

  return (
    <>
      <ContentLayout>
        <TableSection
          showFilter
          showCreateButton={false}
          onSearch={setSearch}
          filter={{
            selected: filter,
            sections: [requestFilterSections, pickupMethodFilterSections],
          }}
          onFilterChange={handleFilterChange}
          rightHeaderActions={
            <TotalCountField
              label="Ukupno zahteva:"
              count={totalResults}
              loading={listLoading}
            />
          }
          columns={columns}
          rowClassName={(row) => (row?.status === 'pending' ? 'pending-request-row' : '')}
          actions={[
            {
              key: 'update-status',
              label: 'Promeni status zahteva',
              icon: (
                <img
                  src="/assets/icons/edit-icon.svg"
                  alt="Izmeni"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: (row) => {
                setSelectedRequest(row);
                setIsStatusModalOpen(true);
              },
              visible: () => isStaff(user),
            },
            {
              key: 'postal-form',
              label: 'Obrazac za poštu',
              icon: (
                <img
                  src="/assets/icons/download.svg"
                  alt="Postal form"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: (row) =>
                postalForm.openForUserId(row?.userId || row?.user?._id),
              visible: () => isStaff(user),
            },
          ]}
          data={requests}
          emptyState={{
            icon: '/assets/icons/no-users.svg',
            title: 'Trenutno nema zahteva',
            description:
              'Zahtevi za preuzimanje ili slanje terapije će biti prikazani ovde',
          }}
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
          searchPlaceholder="Pretraži način / napomenu"
        />
      </ContentLayout>
      <UpdateTherapyOccurrenceRequestStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        request={selectedRequest}
        onUpdated={handleRequestUpdated}
      />
      <PostalFormModal
        isOpen={postalForm.isOpen}
        onClose={postalForm.close}
        user={postalForm.user}
        loading={postalForm.loading}
        error={postalForm.error}
      />
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
